import React, { useEffect, useMemo, useState } from "react";
import "../PlanCreationModal.scss";
import { connect } from "react-redux";
import {
  Card,
  Collapse,
  Fab,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DeleteOutlined as DeleteIcon } from "@mui/icons-material";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import incomeImg from "../../../../assets/plancreation/savings.png";
import arrowDownIcon from "../../../../assets/plancreation/arrowdown.png";
import addIcon from "../../../../assets/add.png";

import Dropdown from "./Dropdown";
import SelectYear from "./SelectYear";
import TextInput from "./TextInput";
import SelectMonth from "./SelectMonth";
import {
  checkDateRangeValid,
  getDefaultFirstToRetireDate,
  getDefaultLastToRetireDate,
  getDefaultLifeExpectancyDate,
  getLifeExpectancyDate,
  getRetireDate,
  hasCoClient,
} from "../utils";
import { getCurrentMonth, getCurrentYear } from "../../../../common/shared";

const columns = [
  { id: "type", label: "Type of income", minWidth: 170 },
  {
    id: "monthly",
    label: "Amount",
    minWidth: 170,
  },
  { id: "owner", label: "Owner", minWidth: 160 },

  {
    id: "timeInterval",
    label: "Time interval",
    minWidth: 300,
  },
];

const IncomesTab = ({
  currentClient,
  updateIncomes,
  incomes,
  changed,
  deleteItem,
  hideBeforeRetirement,
  hideDeathOfSpouse,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [categories, setCategories] = useState(incomes);
  const [selectedCategoryId, setSelectedCategoryId] = useState(-1);

  // client details
  const client = useMemo(() => currentClient.get("client").toJS(), [
    currentClient,
  ]);
  const coClient = useMemo(() => currentClient.get("coClient").toJS(), [
    currentClient,
  ]);

  let isWidowEarlierThanRetirement = false;
  if (coClient && coClient.id !== undefined) {
    const clientRetirementDate = getRetireDate(client, true);
    const coClientLifeExpectancyDate = getLifeExpectancyDate(coClient, true);
    if (clientRetirementDate.getTime() > coClientLifeExpectancyDate.getTime()) {
      isWidowEarlierThanRetirement = true;
    }
  }

  const clients =
    coClient.id !== undefined
      ? [client, coClient, { id: 0, firstName: "Household", lastName: "" }]
      : [client];

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const calculateMaximumPlanLength = (item) => {
    if (item.owner === null) return 30;

    const lifeExpectancyDate = getDefaultLifeExpectancyDate(
      item.owner.id,
      client,
      coClient
    );

    const lifeExpectancyYear = lifeExpectancyDate.getFullYear();
    const currentYear = getCurrentYear();

    return lifeExpectancyYear - currentYear + 1;
  };

  const updateDefaultDates = (category, ownerId) => {
    const lastToRetireDate = getDefaultLastToRetireDate(
      ownerId,
      client,
      coClient
    );
    const firstToRetireDate = getDefaultFirstToRetireDate(
      ownerId,
      client,
      coClient
    );

    const lifeExpectancyDate = getDefaultLifeExpectancyDate(
      ownerId,
      client,
      coClient
    );

    if (category.incomeType.key === "PAYROLL") {
      return {
        ...category,
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: lastToRetireDate.getFullYear(),
        endMonth: lastToRetireDate.getMonth() + 1,
      };
    } else if (
      category.incomeType.key === "BUSINESS" ||
      category.incomeType.key === "OTHER"
    ) {
      return {
        ...category,
        startYear: new Date().getFullYear(),
        startMonth: getCurrentMonth() + 1,
        endYear: lifeExpectancyDate.getFullYear(),
        endMonth: lifeExpectancyDate.getMonth() + 1,
      };
    } else {
      return {
        ...category,
        startYear: firstToRetireDate.getFullYear(),
        startMonth: firstToRetireDate.getMonth() + 1,
        endYear: lifeExpectancyDate.getFullYear(),
        endMonth: lifeExpectancyDate.getMonth() + 1,
      };
    }
  };

  const addNewCategory = () => {
    const len = categories.length;
    if (len === 0 || categories[len - 1].name) {
      setCategories([
        ...categories,
        updateDefaultDates(
          {
            id: null,
            name: "",
            checked: false,
            amount: "0",
            owner: hasCoClient(client, coClient)
              ? { id: 0, firstName: "Household", lastName: "" }
              : {
                  id: client.id,
                  firstName: client.firstName,
                  lastName: client.lastName,
                },
            incomeType: { key: "OTHER" },
            startYear: getCurrentYear(),
            startMonth: getCurrentMonth() + 1,
            endYear: getCurrentYear(),
            endMonth: getCurrentMonth() + 1,
            isEdited: true,
          },
          0
        ),
      ]);
      setSelectedCategoryId(len);
      changed();
    }
  };

  const updateCategory = (itemIndex, fieldName, value) => {
    const newCategories = categories.map((category, index) => {
      if (index !== itemIndex) return category;
      if (fieldName === "name") {
        return {
          ...category,
          name: value,
          isEdited: true,
        };
      } else if (fieldName === "ownerId") {
        const newCategory = updateDefaultDates(category, value);

        return {
          ...newCategory,
          owner: clients.find((c) => c.id === value),
          isEdited: true,
        };
      } else if (fieldName === "amount") {
        return {
          ...category,
          amount: value,
          isEdited: true,
        };
      } else if (fieldName === "startYear") {
        return {
          ...category,
          startYear: Number(value),
          isEdited: true,
        };
      } else if (fieldName === "startMonth") {
        return {
          ...category,
          startMonth: Number(value),
          isEdited: true,
        };
      } else if (fieldName === "endYear") {
        return {
          ...category,
          endYear: Number(value),
          isEdited: true,
        };
      } else if (fieldName === "endMonth") {
        return {
          ...category,
          endMonth: Number(value),
          isEdited: true,
        };
      }
    });
    setCategories([...newCategories]);
    changed();
  };

  const deleteCategory = (index) => {
    deleteItem(categories[index]).then(() => {
      categories.splice(index, 1);
      setCategories([...categories]);
      changed();
    });
  };

  const hasSecondPayroll = () => {
    const payrolls = categories.filter(
      (item) => item.incomeType.key === "PAYROLL"
    );
    return payrolls.length > 1;
  };

  useEffect(() => {
    updateIncomes(categories);
  }, [categories]);

  return (
    <Grid container id="expenses-tab" direction="column" md={9}>
      <Grid item>
        <Typography component="h5">
          Let's calculate your client's income
        </Typography>
      </Grid>
      <Grid item>
        <Card className="monthly-expenses-card">
          <Typography className="highlighted-text" component="h6">
            Estimated Monthly Income
          </Typography>
          <Grid
            container
            spacing={4}
            justifyContent="space-between"
            className="monthly-expenses-inputs"
          >
            {!hideBeforeRetirement && (
              <Grid item>
                <Typography component={"label"}>
                  From now until Retirement
                </Typography>
                <TextInput
                  type="number"
                  id="input-before-retirement"
                  label=""
                  value={categories[0].amount}
                  adornment="$"
                  onChange={(e) => updateCategory(0, "amount", e.target.value)}
                />
              </Grid>
            )}
            <Grid item>
              <Typography component={"label"}>
                During Retirement
                {isWidowEarlierThanRetirement && (
                  <Tooltip
                    title={
                      'Based on the client and co-client\'s life expectancy settings, the widow phase would supersede retirement phase of the surviving client/co-client. Hence, "During Retirement" value is disable. If required, please use the detailed income section to further specify the detailed income items according to the applicable dates.'
                    }
                    placement="right"
                  >
                    <IconButton className={"report-icon"}>
                      <ReportProblemIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Typography>
              <TextInput
                type="number"
                id="input-after-retirement"
                label=""
                value={categories[1].amount}
                adornment="$"
                onChange={(e) => updateCategory(1, "amount", e.target.value)}
                readOnly={isWidowEarlierThanRetirement}
              />
            </Grid>
            {!hideDeathOfSpouse && (
              <Grid item>
                <Typography component={"label"}>Widowed Phase</Typography>
                <Tooltip
                  title={
                    "Widowed Phase refers to the period of life after ones spouse or partner has passed."
                  }
                >
                  <Fab className={"widowed-tooltip"}>?</Fab>
                </Tooltip>
                <TextInput
                  type="number"
                  id="input-after-spouse"
                  label=""
                  value={categories[2].amount}
                  adornment="$"
                  onChange={(e) => updateCategory(2, "amount", e.target.value)}
                />
              </Grid>
            )}
          </Grid>
        </Card>
        <Card className="expenses-detailed-card" onClick={handleExpand}>
          <Grid item xs={12} container direction="row" alignItems="center">
            <Grid item container xs spacing="2" alignItems="center">
              <Grid item>
                <img
                  className="arrow-down-icon"
                  src={arrowDownIcon}
                  alt="arrow-down-icon"
                />
              </Grid>
              <Grid item>
                <img className="bucks-img" src={incomeImg} alt="bucks-img" />
              </Grid>
              <Grid item>
                <Typography component="h6">
                  Add Detailed Monthly Incomes
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Card>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.slice(3).map((category, index) => (
                  <React.Fragment>
                    <TableRow key={index}>
                      <TableCell>
                        <Grid
                          item
                          container
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                        >
                          <Grid item>
                            {selectedCategoryId === index + 3 ? (
                              <TextInput
                                label="New category"
                                value={category.name}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />
                            ) : (
                              <Grid
                                item
                                container
                                direction="row"
                                alignItems="center"
                              >
                                <Typography className="expense-type">
                                  {category.name}
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item>
                          <TextInput
                            type="number"
                            value={category.amount}
                            adornment="$"
                            onChange={(e) =>
                              updateCategory(
                                index + 3,
                                "amount",
                                e.target.value
                              )
                            }
                            isRequired={category.isEdited}
                          />
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item>
                          <Dropdown
                            value={category.owner ? category.owner.id : null}
                            options={clients}
                            onChange={(e) =>
                              updateCategory(
                                index + 3,
                                "ownerId",
                                e.target.value
                              )
                            }
                            disabled={
                              category.incomeType.key === "SOCIAL_SECURITY"
                            }
                            isRequired={category.isEdited}
                          />
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item container direction="row" spacing="3">
                          <Grid item container direction="row">
                            <Grid item>
                              <Typography
                                component="div"
                                className="year-label"
                              >
                                Start &nbsp;
                              </Typography>
                            </Grid>
                            <Grid item>
                              <SelectMonth
                                value={category.startMonth}
                                year={category.startYear}
                                planLength={calculateMaximumPlanLength(
                                  category
                                )}
                                owner={category.owner}
                                client={client}
                                coClient={coClient}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "startMonth",
                                    e.target.value
                                  )
                                }
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                                isRequired={category.isEdited}
                              />
                              /&nbsp;
                              <SelectYear
                                value={category.startYear}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "startYear",
                                    e.target.value
                                  )
                                }
                                length={calculateMaximumPlanLength(category)}
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                                isRequired={category.isEdited}
                              />
                            </Grid>

                            <Grid item>
                              <Typography
                                component="div"
                                className="year-label"
                              >
                                End &nbsp;
                              </Typography>
                            </Grid>
                            <Grid item>
                              <SelectMonth
                                label="End"
                                value={category.endMonth}
                                year={category.endYear}
                                planLength={calculateMaximumPlanLength(
                                  category
                                )}
                                owner={category.owner}
                                client={client}
                                coClient={coClient}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "endMonth",
                                    e.target.value
                                  )
                                }
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                                isRequired={category.isEdited}
                              />
                              /&nbsp;
                              <SelectYear
                                from={category.endYear}
                                value={category.endYear}
                                onChange={(e) =>
                                  updateCategory(
                                    index + 3,
                                    "endYear",
                                    e.target.value
                                  )
                                }
                                length={calculateMaximumPlanLength(category)}
                                error={
                                  !checkDateRangeValid(
                                    category.startYear,
                                    category.startMonth,
                                    category.endYear,
                                    category.endMonth
                                  )
                                }
                                isRequired={category.isEdited}
                              />
                            </Grid>
                            <Grid item>
                              {index > (hasSecondPayroll() ? 7 : 4) && (
                                <IconButton
                                  color="inherit"
                                  className="delete-button"
                                  onClick={() => deleteCategory(index + 3)}
                                  size="large"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid item container xs={12}>
            <Grid
              item
              container
              xs={3}
              direction="row"
              spacing="2"
              className="add-category"
              onClick={addNewCategory}
            >
              <Grid item>
                <img className="add-new" src={addIcon} alt="add-new-icon" />
              </Grid>
              <Grid item>
                <Typography component="div">New Income</Typography>
              </Grid>
            </Grid>
            <Grid item container xs={9} />
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state) => {
  return {
    currentClient: state.currentClientReducer,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(IncomesTab);
